import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Heart, Gift, HandHeart, Calendar, MapPin, Mail, ArrowRight, Loader2,
  ShieldCheck, Sparkles, ExternalLink, Copy,
} from "lucide-react";
import { toast } from "sonner";
import NonprofitQRCard from "@/components/NonprofitQRCard";

/**
 * NonprofitDashboard — the "Give Back" hub for verified 501(c)(3)s on
 * AddVendTour. Brings donations + fundraiser events + volunteer roster
 * into one page so nonprofits can see the impact + follow up personally.
 *
 * Access: only users where `nonprofit_status === "verified"`. Anyone else
 * is bounced to the verification page.
 */
export default function NonprofitDashboard() {
  const { user, loading: authLoading } = useAuth();
  const [donations, setDonations] = useState([]);
  const [fundraisers, setFundraisers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!user || user.nonprofit_status !== "verified") return;
    (async () => {
      try {
        const [donRes, fundRes, sumRes, connRes] = await Promise.all([
          api.get("/nonprofit/donations"),
          api.get("/nonprofit/fundraisers"),
          api.get(`/nonprofits/${user.id}/donations/summary`).catch(() => ({ data: null })),
          api.get("/connect/status").catch(() => ({ data: null })),
        ]);
        setDonations(donRes.data.items || []);
        setFundraisers(fundRes.data.items || []);
        setSummary(sumRes.data);
        setConnectStatus(connRes.data);
      } finally { setLoading(false); }
    })();
  }, [user]);

  if (authLoading) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-20 text-center" data-testid="np-dashboard-authloading">
        <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login?next=/nonprofit/dashboard" replace />;
  if (user.nonprofit_status !== "verified") {
    return <Navigate to="/verify-nonprofit" replace />;
  }
  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-20 text-center" data-testid="np-dashboard-loading">
        <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: "var(--avt-purple)" }} />
      </div>
    );
  }

  const orgName = user.nonprofit_org_name || user.business_name || user.name;
  const totalRaised = summary?.total_raised_cents || 0;
  const totalTips = summary?.total_tip_cents || 0;
  const donorCount = summary?.donor_count || 0;
  const upcoming = fundraisers.filter((f) => f.date && f.date >= new Date().toISOString().slice(0, 10));
  const past = fundraisers.filter((f) => !f.date || f.date < new Date().toISOString().slice(0, 10));

  const startConnect = async () => {
    try {
      const { data } = await api.post("/connect/onboard", {
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch { toast.error("Couldn't start Stripe onboarding"); }
  };

  const copyDonateLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/nonprofits`);
      toast.success("Donate link copied — share it anywhere");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="np-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
               style={{ backgroundColor: "rgba(107,47,186,0.10)", color: "var(--avt-purple)",
                        border: "1.5px solid var(--avt-purple)" }}>
            <ShieldCheck className="h-3 w-3" /> Verified 501(c)(3)
          </div>
          <h1 className="font-serif text-4xl mt-3" data-testid="np-dashboard-title">
            The <span style={{ color: "var(--avt-pink)" }}>Give Back</span> dashboard
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--ink-soft)" }}>
            Every dollar, event, and volunteer for <strong>{orgName}</strong>, in one place.
          </p>
        </div>
        <Button
          onClick={copyDonateLink}
          data-testid="np-share-donate-link"
          className="btn-primary rounded-full"
        >
          <Copy className="h-4 w-4 mr-2" /> Share donate link
        </Button>
      </div>

      {/* Connect gate */}
      {!connectStatus?.charges_enabled && (
        <div className="mt-6 rounded-xl p-5 border-2 flex items-center justify-between gap-4 flex-wrap"
             data-testid="np-connect-gate"
             style={{ borderColor: "var(--avt-orange)", backgroundColor: "rgba(249,115,22,0.06)" }}>
          <div>
            <div className="font-semibold text-base">Finish setting up donations</div>
            <p className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>
              Connect your Stripe account so donations land in your bank &mdash; not ours. Takes about 3 minutes.
            </p>
          </div>
          <Button onClick={startConnect} data-testid="np-connect-cta" className="btn-primary rounded-full">
            <Sparkles className="h-4 w-4 mr-2" /> Connect Stripe
          </Button>
        </div>
      )}

      {/* Impact tiles */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="np-dashboard-tiles">
        <Tile icon={Heart} color="var(--avt-pink)" label="Raised (all time)"
              value={`$${(totalRaised / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              testId="np-tile-raised" />
        <Tile icon={Gift} color="var(--avt-purple)" label="Supporters"
              value={donorCount} testId="np-tile-donors" />
        <Tile icon={Calendar} color="var(--avt-teal)" label="Upcoming fundraisers"
              value={upcoming.length} testId="np-tile-upcoming" />
        <Tile icon={Sparkles} color="var(--avt-orange)" label="Optional tips"
              value={`$${(totalTips / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
              hint="What supporters chipped in to AddVendTour"
              testId="np-tile-tips" />
      </div>

      {/* Donor QR — print on event tables, mailers, thank-you cards. */}
      <div className="mt-6">
        <NonprofitQRCard slug={user.slug} orgName={orgName} />
      </div>

      {/* Tabs */}
      <div className="mt-8 flex items-center gap-2 border-b overflow-x-auto"
           style={{ borderColor: "var(--border-warm)" }} data-testid="np-dashboard-tabs">
        {[
          { key: "overview", label: "Overview" },
          { key: "donations", label: `Donations · ${donations.length}` },
          { key: "fundraisers", label: `Fundraisers · ${fundraisers.length}` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`np-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className="px-4 py-2 text-sm font-semibold transition-colors"
            style={{
              color: tab === t.key ? "var(--avt-purple)" : "var(--ink-soft)",
              borderBottom: tab === t.key ? "2px solid var(--avt-purple)" : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="mt-6 grid md:grid-cols-2 gap-6" data-testid="np-tab-content-overview">
          {/* Recent donations */}
          <section className="card-warm rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div className="overline" style={{ color: "var(--avt-pink)" }}>Recent donations</div>
              {donations.length > 5 && (
                <button onClick={() => setTab("donations")}
                        className="text-xs underline" style={{ color: "var(--avt-purple)" }}>
                  See all
                </button>
              )}
            </div>
            {donations.length === 0 ? (
              <p className="mt-3 text-sm italic" style={{ color: "var(--ink-soft)" }}>
                No donations yet. Share your donate link on social &mdash; supporters land on the
                <Link to="/nonprofits" className="mx-1 underline" style={{ color: "var(--avt-purple)" }}>Nonprofits directory</Link>
                and can give in one tap.
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {donations.slice(0, 5).map((d) => (
                  <li key={d.id} className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0"
                         style={{ backgroundColor: "rgba(236,72,153,0.14)" }}>
                      <Heart className="h-4 w-4" style={{ color: "var(--avt-pink)" }} fill="var(--avt-pink)" strokeWidth={0} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-semibold truncate">{d.donor_name}</span>
                        <span className="font-serif text-lg" style={{ color: "var(--avt-purple)" }}>
                          ${(d.amount_cents / 100).toFixed(0)}
                        </span>
                      </div>
                      {d.message && (
                        <p className="text-xs italic" style={{ color: "var(--ink-soft)" }}>
                          &ldquo;{d.message}&rdquo;
                        </p>
                      )}
                      <a href={`mailto:${d.donor_email}`}
                         className="mt-1 inline-flex items-center gap-1 text-[11px]"
                         style={{ color: "var(--avt-teal)" }}>
                        <Mail className="h-3 w-3" /> Say thanks
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Upcoming fundraisers */}
          <section className="card-warm rounded-2xl p-6">
            <div className="overline" style={{ color: "var(--avt-teal)" }}>Upcoming fundraisers</div>
            {upcoming.length === 0 ? (
              <div className="mt-3 space-y-3">
                <p className="text-sm italic" style={{ color: "var(--ink-soft)" }}>
                  No upcoming fundraisers benefiting your org yet.
                </p>
                <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
                  Any organizer can flag their event as a fundraiser and pick you as the beneficiary.
                  Send them your donate link!
                </p>
              </div>
            ) : (
              <ul className="mt-4 space-y-3">
                {upcoming.slice(0, 5).map((f) => (
                  <FundraiserRow key={f.id} f={f} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {/* Donations tab */}
      {tab === "donations" && (
        <div className="mt-6" data-testid="np-tab-content-donations">
          {donations.length === 0 ? (
            <p className="text-sm italic" style={{ color: "var(--ink-soft)" }}>No donations yet.</p>
          ) : (
            <div className="overflow-x-auto card-warm rounded-2xl">
              <table className="min-w-full text-sm" data-testid="np-donations-table">
                <thead style={{ backgroundColor: "var(--sand)" }}>
                  <tr>
                    <Th>Donor</Th><Th>Amount</Th><Th>Tip</Th><Th>Event</Th><Th>Date</Th><Th>Reach out</Th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.id} className="border-t" style={{ borderColor: "var(--border-warm)" }}>
                      <Td>
                        <div className="font-semibold">{d.donor_name}</div>
                        {d.message && (
                          <div className="text-xs italic" style={{ color: "var(--ink-soft)" }}>
                            &ldquo;{d.message}&rdquo;
                          </div>
                        )}
                      </Td>
                      <Td>
                        <span className="font-serif text-base" style={{ color: "var(--avt-purple)" }}>
                          ${(d.amount_cents / 100).toFixed(2)}
                        </span>
                      </Td>
                      <Td>
                        {d.tip_cents > 0 ? `$${(d.tip_cents / 100).toFixed(2)}` : "—"}
                      </Td>
                      <Td>
                        {d.event_id ? (
                          <Link to={`/events/${d.event_id}`}
                                className="underline inline-flex items-center gap-1 text-xs"
                                style={{ color: "var(--avt-teal)" }}>
                            View <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--ink-soft)" }}>Direct</span>
                        )}
                      </Td>
                      <Td>
                        <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
                          {(d.created_at || "").slice(0, 10)}
                        </span>
                      </Td>
                      <Td>
                        <a href={`mailto:${d.donor_email}?subject=Thank%20you%20from%20${encodeURIComponent(orgName)}`}
                           className="text-xs inline-flex items-center gap-1"
                           style={{ color: "var(--avt-purple)" }}>
                          <Mail className="h-3 w-3" /> Thank
                        </a>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Fundraisers tab */}
      {tab === "fundraisers" && (
        <div className="mt-6 space-y-6" data-testid="np-tab-content-fundraisers">
          {upcoming.length > 0 && (
            <div>
              <div className="overline mb-3" style={{ color: "var(--avt-teal)" }}>Upcoming</div>
              <ul className="space-y-3">
                {upcoming.map((f) => <FundraiserRow key={f.id} f={f} expanded />)}
              </ul>
            </div>
          )}
          {past.length > 0 && (
            <div>
              <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>Past</div>
              <ul className="space-y-3">
                {past.map((f) => <FundraiserRow key={f.id} f={f} expanded />)}
              </ul>
            </div>
          )}
          {fundraisers.length === 0 && (
            <p className="text-sm italic" style={{ color: "var(--ink-soft)" }}>
              No fundraiser events benefiting your org yet. Ask an organizer to flag their event
              as a fundraiser and pick you as the beneficiary.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, color, label, value, hint, testId }) {
  return (
    <div className="card-warm rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full flex items-center justify-center"
             style={{ backgroundColor: `${color}22` }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{label}</div>
      </div>
      <div className="mt-2 font-serif text-2xl" style={{ color }}>{value}</div>
      {hint && <div className="text-[10px] mt-1" style={{ color: "var(--ink-soft)" }}>{hint}</div>}
    </div>
  );
}

function FundraiserRow({ f, expanded }) {
  const raised = f.fundraising_raised_cents || 0;
  const goal = f.fundraising_goal_cents;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : null;
  return (
    <li className="p-3 rounded-lg border flex items-center gap-3"
        style={{ borderColor: "var(--border-warm)", backgroundColor: "var(--sand)" }}
        data-testid={`np-fundraiser-${f.id}`}>
      {expanded && f.cover_image && (
        <img src={f.cover_image} alt="" className="h-14 w-14 rounded-lg object-cover flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <Link to={`/events/${f.id}`} className="font-semibold text-sm truncate hover:underline"
                style={{ color: "var(--ink)" }}>
            {f.title}
          </Link>
          <span className="font-serif text-base" style={{ color: "var(--avt-purple)" }}>
            ${(raised / 100).toLocaleString()}
          </span>
        </div>
        <div className="text-xs flex items-center gap-2 mt-0.5" style={{ color: "var(--ink-soft)" }}>
          <Calendar className="h-3 w-3" />{f.date}
          {f.city && <><MapPin className="h-3 w-3" />{f.city}</>}
          <span>·</span>
          <span>{f.fundraising_donor_count || 0} supporters</span>
        </div>
        {pct !== null && (
          <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0,0,0,0.06)" }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: "linear-gradient(90deg, var(--avt-pink), var(--avt-purple))",
            }} />
          </div>
        )}
      </div>
      <Link to={`/events/${f.id}`} className="text-xs"
            style={{ color: "var(--avt-teal)" }}>
        <ArrowRight className="h-4 w-4" />
      </Link>
    </li>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest"
             style={{ color: "var(--ink-soft)" }}>{children}</th>;
}
function Td({ children }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}
